import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, setAuthToken } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pgm_user") || "null"); } catch (e) { return null; }
  });
  const [loading, setLoading] = useState(false);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setAuthToken(data.token);
    try { localStorage.setItem("pgm_user", JSON.stringify(data.user)); } catch (e) { console.warn("Local cache write failed:", e); }
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("Logout call failed", e); }
    setAuthToken(null);
    try { localStorage.removeItem("pgm_user"); } catch (e) { console.warn("Local cache write failed:", e); }
    setUser(null);
    if (typeof window !== "undefined") window.location.href = "/login";
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      try { localStorage.setItem("pgm_user", JSON.stringify(data)); } catch (e) { console.warn("Local cache write failed:", e); }
    } catch (e) {
      console.warn("Session refresh failed; user may be logged out", e?.response?.status);
    }
  }, []);

  const impersonate = useCallback(async (userId) => {
    const { data } = await api.post(`/admin/impersonate/${userId}`);
    setAuthToken(data.token);
    try { localStorage.setItem("pgm_user", JSON.stringify(data.user)); } catch (e) { console.warn("Local cache write failed:", e); }
    setUser(data.user);
    return data.user;
  }, []);

  const stopImpersonating = useCallback(async () => {
    const { data } = await api.post("/auth/stop-impersonation");
    setAuthToken(data.token);
    try { localStorage.setItem("pgm_user", JSON.stringify(data.user)); } catch (e) { console.warn("Local cache write failed:", e); }
    setUser(data.user);
    return data.user;
  }, []);

  // On mount, if we think we're logged in (cached user), verify the cookie is still valid.
  // CRITICAL: If we're in the middle of an OAuth callback (URL has session_id=), skip /auth/me
  // and let AuthCallback handle the session exchange first.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash && window.location.hash.includes("session_id=")) {
      return;
    }
    if (user) refreshMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refreshMe, setUser, impersonate, stopImpersonating }),
    [user, loading, login, logout, refreshMe, impersonate, stopImpersonating]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
